import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Scoreboard.module.css";
import axios from "axios";

type Sport = "football" | "basketball" | "volleyball";

const sports: Sport[] = ["football", "basketball", "volleyball"];

const tableColumns: Record<string, string[]> = {
  football: ["P", "W", "D", "L", "GF", "GA", "GD", "Pts"],
  basketball: ["P", "W", "D", "L", "PF", "PA", "PD", "Pts"],
  volleyball: ["P", "W", "L", "SF", "SA", "SD", "Pts"],
};

const API_URL = process.env.REACT_APP_API_URL;

const Ranking: React.FC = () => {
  const navigate = useNavigate();

  const [table, setTable] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState<Sport>("football");

  useEffect(() => {
    const fetchTable = async () => {
      setLoading(true);

      try {
        const res = await axios.post(`${API_URL}/get-sports-table`, {
          token: localStorage.getItem("token"),
          sport: selectedSport,
        });

        setTable(res.data?.data || {});
      } catch (err) {
        console.error("Failed to fetch table:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTable();
  }, [selectedSport]);

  const navigateToHome = useCallback(() => navigate("/"), [navigate]);

  const handleSportChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSport(e.target.value as Sport);
  };

  /**
   * GROUP TEAMS BY GROUP LETTER
   */
  const groupTeams = (teams: any[]) => {
    const groups: Record<string, any[]> = {};

    teams.forEach((team) => {
      const group = team.group || "Unknown";

      if (!groups[group]) groups[group] = [];
      groups[group].push(team);
    });

    return groups;
  };

  /**
   * TABLE RENDER
   */
  const renderTable = (teams: any[]) => (
    <div className={styles.tableWrapper}>
      <table className={styles.leagueTable}>
        <thead>
          <tr>
            <th>Pos</th>
            <th>Team</th>

            {tableColumns[selectedSport].map((col) => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {teams.map((team: any, index: number) => {
            if (selectedSport === "football") {
              const gd = team.goalsFor - team.goalsAgainst;

              return (
                <tr key={team.name}>
                  <td>{index + 1}</td>
                  <td>{team.name}</td>
                  <td>{team.played}</td>
                  <td>{team.wins}</td>
                  <td>{team.draws}</td>
                  <td>{team.losses}</td>
                  <td>{team.goalsFor}</td>
                  <td>{team.goalsAgainst}</td>
                  <td>{gd}</td>
                  <td>{team.points}</td>
                </tr>
              );
            }

            if (selectedSport === "basketball") {
              const pd = team.pointsFor - team.pointsAgainst;

              return (
                <tr key={team.name}>
                  <td>{index + 1}</td>
                  <td>{team.name}</td>
                  <td>{team.played}</td>
                  <td>{team.wins}</td>
                  <td>{team.draws}</td>
                  <td>{team.losses}</td>
                  <td>{team.pointsFor}</td>
                  <td>{team.pointsAgainst}</td>
                  <td>{pd}</td>
                  <td>{team.points}</td>
                </tr>
              );
            }

            if (selectedSport === "volleyball") {
              const sd = team.setsFor - team.setsAgainst;

              return (
                <tr key={team.name}>
                  <td>{index + 1}</td>
                  <td>{team.name}</td>
                  <td>{team.played}</td>
                  <td>{team.wins}</td>
                  <td>{team.losses}</td>
                  <td>{team.setsFor}</td>
                  <td>{team.setsAgainst}</td>
                  <td>{sd}</td>
                  <td>{team.points}</td>
                </tr>
              );
            }

            return null;
          })}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return <div className={styles.scoreboardContainer}>Loading rankings...</div>;
  }

  const teams = table.table || [];

  return (
    <div className={styles.scoreboardContainer}>
      <section className={styles.currentMatchSection}>
        <h2 className={styles.currentSportTitle}>LEADERBOARD</h2>

        <div className={styles.sportSelector}>
          <label>Select Sport:</label>

          <select
            value={selectedSport}
            onChange={handleSportChange}
            className={styles.dropdown}
          >
            {sports.map((sport) => (
              <option key={sport} value={sport}>
                {sport.replace("_", " ").toUpperCase()}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className={styles.leaderboardSection}>
        <h3 className={styles.leaderboardTitle}>
          {selectedSport.replace("_", " ").toUpperCase()} Table
        </h3>

        {/* FOOTBALL */}
        {selectedSport === "football" && renderTable(teams)}

        {/* BASKETBALL */}
        {selectedSport === "basketball" && (
          <>
            <h3>Basketball (Male)</h3>
            {Object.entries(groupTeams(table.male || [])).sort(([a], [b]) => a.localeCompare(b)).map(([group, groupTeams]) => (
              <div key={`male-${group}`}>
                <h4>Group {group.toUpperCase()}</h4>
                {renderTable(groupTeams)}
              </div>
            ))}

            <h3>Basketball (Female)</h3>
            {Object.entries(groupTeams(table.female || [])).sort(([a], [b]) => a.localeCompare(b)).map(([group, groupTeams]) => (
              <div key={`female-${group}`}>
                <h4>Group {group.toUpperCase()}</h4>
                {renderTable(groupTeams)}
              </div>
            ))}
          </>
        )}

        {/* VOLLEYBALL */}
        {selectedSport === "volleyball" &&
          Object.entries(groupTeams(table.table || [])).sort(([a], [b]) => a.localeCompare(b)).map(([group, groupTeams]) => (
            <div key={group}>
              <h4>Group {group.toUpperCase()}</h4>
              {renderTable(groupTeams)}
            </div>
        ))}

        <button className={styles.viewLink} onClick={navigateToHome}>
          Back to Home
        </button>
      </section>
    </div>
  );
};

export default Ranking;